
import React, { useState, useEffect } from 'react';
import { Activity, BadgeDollarSign, Users, UserPlus } from 'lucide-react';
import { MonthSelector } from './MonthSelector';
import { MonthlyMetricCard } from './MonthlyMetricCard';
import { MetricsChart } from './MetricsChart';
import { MetricConfig, MonthlyMetric } from './types';
import { getMonthlyMetrics } from '@/services/analytics/monthlyMetricsService';

const METRIC_CONFIGS: Record<string, MetricConfig> = {
  bikeUtilization: {
    title: 'Активность парка',
    icon: Activity,
    valueFormatter: (value: number) => `${value.toFixed(1)}%`,
    color: 'text-tuktuk-teal',
    growthLabel: 'к прошлому месяцу'
  },
  revenue: {
    title: 'Доход за месяц',
    icon: BadgeDollarSign,
    valueFormatter: (value: number) => `₽${new Intl.NumberFormat('ru-RU').format(value)}`,
    color: 'text-tuktuk-teal',
    growthLabel: 'к прошлому месяцу'
  },
  activeCouriers: {
    title: 'Оплатило Курьеров',
    icon: Users,
    valueFormatter: (value: number) => value.toString(),
    color: 'text-tuktuk-teal',
    growthLabel: 'к прошлому месяцу'
  },
  newCustomers: {
    title: 'Новые клиенты',
    icon: UserPlus,
    valueFormatter: (value: number) => value.toString(),
    color: 'text-tuktuk-teal',
    growthLabel: 'к прошлому месяцу'
  },
  repeatCustomers: {
    title: 'Повторные клиенты',
    icon: Users,
    valueFormatter: (value: number) => value.toString(),
    color: 'text-tuktuk-teal',
    growthLabel: 'к прошлому месяцу'
  }
};

export const MonthlyMetricsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [allMetrics, setAllMetrics] = useState<{
    bikeUtilization: MonthlyMetric[];
    revenue: MonthlyMetric[];
    activeCouriers: MonthlyMetric[];
    newCustomers: MonthlyMetric[];
    repeatCustomers: MonthlyMetric[];
  }>({
    bikeUtilization: [],
    revenue: [],
    activeCouriers: [],
    newCustomers: [],
    repeatCustomers: []
  });

  const loadMetrics = async () => {
    setLoading(true);
    try {
      console.log('Загрузка динамических месячных метрик...');
      const data = await getMonthlyMetrics(12);
      setAllMetrics(data);
      
      // Автоматически выбираем последний доступный месяц, если текущий месяц не найден
      const allAvailableMetrics = [...data.bikeUtilization, ...data.revenue, ...data.activeCouriers, ...data.newCustomers, ...data.repeatCustomers];
      if (allAvailableMetrics.length > 0) {
        const latestMetric = allAvailableMetrics
          .sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return b.month - a.month;
          })[0];
        
        // Если текущий выбранный месяц не найден в данных, выбираем последний доступный
        const currentExists = allAvailableMetrics.some(m => 
          m.year === selectedYear && m.month === selectedMonth
        );
        
        if (!currentExists) {
          setSelectedYear(latestMetric.year);
          setSelectedMonth(latestMetric.month);
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке месячной аналитики:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  const getCurrentMetric = (metrics: MonthlyMetric[]): MonthlyMetric | null => {
    return metrics.find(m => m.year === selectedYear && m.month === selectedMonth) || null;
  };

  const handleMonthChange = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  // Собираем все доступные месяцы из всех метрик
  const getAllAvailableMonths = () => {
    const monthsSet = new Set<string>();
    
    [...allMetrics.bikeUtilization, ...allMetrics.revenue, ...allMetrics.activeCouriers, ...allMetrics.newCustomers, ...allMetrics.repeatCustomers]
      .forEach(metric => {
        monthsSet.add(`${metric.year}-${metric.month}`);
      });
    
    return Array.from(monthsSet)
      .map(key => {
        const [year, month] = key.split('-').map(Number);
        return { year, month };
      })
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
  };

  const availableMonths = getAllAvailableMonths();

  return (
    <div className="space-y-6">
      {/* Селектор месяца */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Динамика показателей</h2>
        <MonthSelector
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
          availableMonths={availableMonths}
        />
      </div>

      {/* Информация о загруженных данных */}
      {!loading && (
        <div className="text-sm text-muted-foreground">
          Загружено данных: активность парка ({allMetrics.bikeUtilization.length} мес.), 
          доходы ({allMetrics.revenue.length} мес.), 
          оплатило курьеров ({allMetrics.activeCouriers.length} мес.),
          новые клиенты ({allMetrics.newCustomers.length} мес.),
          повторные клиенты ({allMetrics.repeatCustomers.length} мес.)
        </div>
      )}

      {/* Карточки с показателями за выбранный месяц */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <MonthlyMetricCard
          metric={getCurrentMetric(allMetrics.bikeUtilization)}
          config={METRIC_CONFIGS.bikeUtilization}
          loading={loading}
        />
        <MonthlyMetricCard
          metric={getCurrentMetric(allMetrics.revenue)}
          config={METRIC_CONFIGS.revenue}
          loading={loading}
        />
        <MonthlyMetricCard
          metric={getCurrentMetric(allMetrics.activeCouriers)}
          config={METRIC_CONFIGS.activeCouriers}
          loading={loading}
        />
        <MonthlyMetricCard
          metric={getCurrentMetric(allMetrics.newCustomers)}
          config={METRIC_CONFIGS.newCustomers}
          loading={loading}
        />
        <MonthlyMetricCard
          metric={getCurrentMetric(allMetrics.repeatCustomers)}
          config={METRIC_CONFIGS.repeatCustomers}
          loading={loading}
        />
      </div>

      {/* Графики динамики */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MetricsChart
          data={allMetrics.bikeUtilization}
          title="Активность парка"
          valueFormatter={METRIC_CONFIGS.bikeUtilization.valueFormatter}
          color="hsl(var(--primary))"
          loading={loading}
        />
        <MetricsChart
          data={allMetrics.revenue}
          title="Доход"
          valueFormatter={METRIC_CONFIGS.revenue.valueFormatter}
          color="hsl(var(--primary))"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MetricsChart
          data={allMetrics.activeCouriers}
          title="Оплатило Курьеров"
          valueFormatter={METRIC_CONFIGS.activeCouriers.valueFormatter}
          color="hsl(var(--primary))"
          loading={loading}
        />
      </div>

      {/* Графики клиентов */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MetricsChart
          data={allMetrics.newCustomers}
          title="Динамика новых клиентов"
          valueFormatter={METRIC_CONFIGS.newCustomers.valueFormatter}
          color="hsl(var(--primary))"
          loading={loading}
        />
        <MetricsChart
          data={allMetrics.repeatCustomers}
          title="Динамика повторных клиентов"
          valueFormatter={METRIC_CONFIGS.repeatCustomers.valueFormatter}
          color="hsl(var(--primary))"
          loading={loading}
        />
      </div>
    </div>
  );
};
